import { useCallback, useMemo } from 'react';

import useToggle from '@lyearn/core/hooks/useToggle';
import ErrorReporter from '@lyearn/core/utils/ErrorReporter';
import { useUppy } from '@lyearn/shared/hooks/useUppy';
import useDebounceState from '@/hooks/useDebounceState';
import { trackDownload } from '@/hooks/useUnsplash';
import { DesignCard } from '@/modules/corporate/components/Design/enums';
import { CoverType } from '@/modules/corporate/components/ImageList/types';

import { getUploadedCoverItems } from '../../../modules/course/views/CourseBuilder/utils';

const useDesignCoverActions = (
  setOverlayContent: ({ type, picture }: { type: DesignCard; picture?: string }) => void,
  previewCover: CoverType,
  setPreviewCover: (cover: CoverType) => void,
  onSubmitCover: (url: string, name: string) => Promise<boolean>,
) => {
  const [searchInput, setSearchInput, searchString] = useDebounceState('');
  const [isSettingCover, { toggle: toggleSettingCover }] = useToggle(false);
  const { files, uppy, clearAll } = useUppy({ referenceId: 'design-cover' });

  const uploadedItems: CoverType[] = useMemo(() => getUploadedCoverItems(files), [files]);

  const hideOverlay = useCallback(() => {
    setOverlayContent({ type: DesignCard.None });
    setPreviewCover({ url: previewCover?.url ?? '', name: '' });
    clearAll();
  }, [clearAll, previewCover?.url, setOverlayContent, setPreviewCover]);

  const setCoverImage = useCallback(async () => {
    toggleSettingCover();
    const isCoverSet = await onSubmitCover(previewCover.url, previewCover.name);
    if (isCoverSet) {
      if (previewCover.downloadLocation) {
        try {
          trackDownload(previewCover.downloadLocation);
        } catch (error) {
          ErrorReporter.error(error);
        }
      }
    }
    toggleSettingCover();
  }, [
    onSubmitCover,
    previewCover.downloadLocation,
    previewCover.name,
    previewCover.url,
    setOverlayContent,
    toggleSettingCover,
  ]);

  return {
    selectedCard: previewCover,
    searchInput,
    setSearchInput,
    searchString,
    isSettingCover,
    uppy,
    uploadedItems,
    setCard: setPreviewCover,
    hideOverlay,
    setCoverImage,
  };
};

export default useDesignCoverActions;
